/*
 * `?thread=<id>&message=<id>` on the workspace URL (mention notifications and
 * emails link here): open that thread, then scroll to and briefly highlight
 * the message once the transcript has loaded. `?thread=<id>&run=<id>` (a
 * skill's "Run on this case" from Firm Knowledge): open the thread and send
 * the run's seed message once. `message` and `run` are dropped after use so a
 * refresh does not re-run them; `thread` stays and follows the open thread
 * (`useThreadInUrl`), so a reload or a shared link reopens it.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { THREAD_PARAM, writeSearchParams } from "../urlState";

import type { CaseAgentUIMessage } from "./useCaseAgentChat";

export { THREAD_PARAM };
export const MESSAGE_PARAM = "message";
export const RUN_PARAM = "run";
export const HIGHLIGHT_MS = 4000;

export type ThreadDeepLink = {
  threadId: string | null;
  messageId: string | null;
  /** The skill run whose seed message this page should send. */
  runId: string | null;
};

/** Read (once) the deep-link params and clear the one-shot ones. */
export const useThreadDeepLink = (): ThreadDeepLink => {
  const [params, setParams] = useSearchParams();
  // Captured on first render: the params are cleared right after.
  const initial = useRef<ThreadDeepLink>({
    threadId: params.get(THREAD_PARAM),
    messageId: params.get(MESSAGE_PARAM),
    runId: params.get(RUN_PARAM)
  });
  useEffect(() => {
    if (!params.has(MESSAGE_PARAM) && !params.has(RUN_PARAM)) return;
    const next = new URLSearchParams(params);
    next.delete(MESSAGE_PARAM);
    next.delete(RUN_PARAM);
    setParams(next, { replace: true });
    // Once, on mount.
  }, []);
  return initial.current;
};

/**
 * Once the linked message is in the loaded transcript, scroll it into view and
 * return its id for `HIGHLIGHT_MS`. Fires at most once per page load.
 */
export const useMessageHighlight = (
  messageId: string | null,
  messages: CaseAgentUIMessage[],
  restoring: boolean
): string | null => {
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const consumed = useRef(false);
  const present =
    !!messageId &&
    !consumed.current &&
    !restoring &&
    messages.some((m) => m.id === messageId);
  useEffect(() => {
    if (!present || !messageId) return undefined;
    consumed.current = true;
    setHighlighted(messageId);
    // Next frame so the row exists in the DOM.
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(`msg-${messageId}`)
        ?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    });
    const timer = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [present, messageId]);
  return highlighted;
};

/**
 * Mirror the open thread into `?thread=`. Nothing is written until a thread
 * has been open, so a deep link isn't cleared while the chat is still
 * selecting it; "New Chat" (no thread) drops the param.
 */
export const useThreadInUrl = (threadId: string | null): void => {
  const seen = useRef(false);
  useEffect(() => {
    if (threadId) seen.current = true;
    if (seen.current) writeSearchParams({ [THREAD_PARAM]: threadId });
  }, [threadId]);
};
